import { searchContent, getLabels } from './confluence';
import { LIMIT_QUESTIONS } from './limits';
import { QUESTION_TYPE } from '../constants/custom-content';
import { POPULAR, UNANSWERED } from '../constants/filters';
import { QUESTION_EXPANSIONS } from './question';

export const EXPAND_ALL =
	'' +
	'space,' +
	'metadata.labels,' +
	'history.createdBy,' +
	'history.lastUpdated.when,' +
	'children.ac__com_atlassian_confluence_plugins_confluence_questions__answer,' +
	'children.ac__com_atlassian_confluence_plugins_confluence_questions__answer.history.createdBy,' +
	'children.comment,' +
	'children.comment.history.createdBy,' +
	'metadata.properties.atl_cq_votes,' +
	'children.ac__com_atlassian_confluence_plugins_confluence_questions__answer.metadata.properties.atl_cq_votes,' +
	'metadata.properties.atl_cq_accepted_answer,' +
	'children.ac__com_atlassian_confluence_plugins_confluence_questions__answer.metadata.properties.atl_cq_accepted_answer';

export const EXPAND_ALL_WITH_TOTAL_VOTES = `${EXPAND_ALL},metadata.properties.atl_cq_votes_total`;

export const ORDER_BY_CREATED = 'created desc, title';

export const ORDER_BY_TOTAL_VOTES = 'atl_cq_votes_total desc, lastmodified desc';

export const addOrderBy = (cql, orderBy) => {
	return `${cql} order by ${orderBy}`;
};

export const buildRequest = ({
	expand,
	cql,
	pageNumber = 0,
	cursor = '',
	limit = LIMIT_QUESTIONS,
}) => {
	const requestObject = {
		expand,
		cql,
		limit,
	};

	// there are numerous initial load fetch calls
	// and we don't want to initiate a call with the cursor if the pageNumber is initially 0,
	// so keeping the `pageNumber` argument in all of the callees
	if (pageNumber) {
		requestObject.cursor = cursor;
	}

	return requestObject;
};

export const getSpaceKeyFilter = (spaceKey, isGlobalQuestionsPage) => {
	return isGlobalQuestionsPage === true ? '' : `space="${spaceKey}" AND `;
};

export const getUnansweredQuestions = (pageNumber, spaceKey, isGlobalQuestionsPage, cursor) => {
	const query = `${getSpaceKeyFilter(spaceKey, isGlobalQuestionsPage)}
    type="${QUESTION_TYPE}"
    AND content.property[atl_cq_has_answers].any_answer=false
  `;
	const cql = addOrderBy(query, ORDER_BY_CREATED);

	return searchContent(
		buildRequest({
			expand: EXPAND_ALL,
			cql,
			pageNumber,
			cursor,
		}),
	);
};

export const getUnansweredQuestionsByTopic = (
	pageNumber,
	topic,
	spaceKey,
	isGlobalQuestionsPage,
	cursor,
) => {
	const query = `${getSpaceKeyFilter(spaceKey, isGlobalQuestionsPage)}
    type="${QUESTION_TYPE}"
    AND content.property[atl_cq_has_answers].any_answer=false
    AND label="${topic}"
  `;
	const cql = addOrderBy(query, ORDER_BY_CREATED);

	return searchContent(
		buildRequest({
			expand: EXPAND_ALL,
			cql,
			pageNumber,
			cursor,
		}),
	);
};

export const getRecentQuestions = (pageNumber, spaceKey, isGlobalQuestionsPage, cursor) => {
	const query = `${getSpaceKeyFilter(spaceKey, isGlobalQuestionsPage)}type="${QUESTION_TYPE}"`;
	const cql = addOrderBy(query, ORDER_BY_CREATED);

	return searchContent(
		buildRequest({
			expand: EXPAND_ALL,
			cql,
			pageNumber,
			cursor,
		}),
	);
};

export const getRecentQuestionsByTopic = (
	pageNumber,
	topic,
	spaceKey,
	isGlobalQuestionsPage,
	cursor,
) => {
	const query = `${getSpaceKeyFilter(spaceKey, isGlobalQuestionsPage)}
    type="${QUESTION_TYPE}"
    AND label="${topic}"
  `;
	const cql = addOrderBy(query, ORDER_BY_CREATED);

	return searchContent(
		buildRequest({
			expand: EXPAND_ALL,
			cql,
			pageNumber,
			cursor,
		}),
	);
};

export const getPopularQuestions = (pageNumber, spaceKey, isGlobalQuestionsPage, cursor) => {
	const query = `${getSpaceKeyFilter(spaceKey, isGlobalQuestionsPage)}
    type="${QUESTION_TYPE}"
    AND atl_cq_votes_total > 0
  `;
	const cql = addOrderBy(query, ORDER_BY_TOTAL_VOTES);

	return searchContent(
		buildRequest({
			expand: EXPAND_ALL_WITH_TOTAL_VOTES,
			cql,
			pageNumber,
			cursor,
		}),
	);
};

export const getPopularQuestionsByTopic = (
	pageNumber,
	topic,
	spaceKey,
	isGlobalQuestionsPage,
	cursor,
) => {
	const query = `${getSpaceKeyFilter(spaceKey, isGlobalQuestionsPage)}
    type="${QUESTION_TYPE}"
    AND label="${topic}"
    AND atl_cq_votes_total > 0
  `;
	const cql = addOrderBy(query, ORDER_BY_TOTAL_VOTES);

	return searchContent(
		buildRequest({
			expand: EXPAND_ALL_WITH_TOTAL_VOTES,
			cql,
			pageNumber,
			cursor,
		}),
	);
};

export const getRelatedQuestions = (spaceKey, questionId, isGlobalQuestionsPage) => {
	// getQuestion topic, searchContent questions for topic
	return getLabels(questionId).then((data) => {
		// Content must have one label at least
		// labels that have quotes break the CQL expression
		const labels = data.results
			.filter((label) => label.name.indexOf('"') == -1)
			.map((label) => `"${label.name}"`)
			.join(',');

		const cql = `${getSpaceKeyFilter(spaceKey, isGlobalQuestionsPage)}
        type="${QUESTION_TYPE}"
        AND label IN (${labels})
        AND id!=${questionId}
      `;
		return searchContent({
			expand: EXPAND_ALL,
			cql,
		});
	});
};

export const getQuestions = (pageNumber, filter, topics, allTopics, spaceKey, limit, cursor) => {
	const conditions = [];
	const addConditionIf = (i, condition) => (i ? conditions.push(condition) : null);

	addConditionIf(true, `type="${QUESTION_TYPE}"`);
	addConditionIf(spaceKey, `space="${spaceKey}"`);
	addConditionIf(filter === POPULAR, 'atl_cq_votes_total > 0');
	addConditionIf(filter === UNANSWERED, 'content.property[atl_cq_has_answers].any_answer=false');
	addConditionIf(topics.length && allTopics, `label="${topics.join('" AND label="')}"`);
	addConditionIf(topics.length && !allTopics, `label in (${topics.join(',')})`);

	const query = conditions.join(' AND ');
	const cql = addOrderBy(query, ORDER_BY_CREATED);

	return searchContent(
		buildRequest({
			expand: EXPAND_ALL,
			cql,
			pageNumber,
			limit,
			cursor,
		}),
	);
};

export const composeSearchCQL = (searchString, spaceKey) => {
	let cql;
	cql = `type="${QUESTION_TYPE}" AND title~"${searchString}*"`;
	if (spaceKey) {
		cql = `${cql} AND space="${spaceKey}"`;
	}
	return cql;
};

export const searchQuestionsByTitle = (searchString, includeBody, spaceKey, cursor) => {
	const query = composeSearchCQL(searchString, spaceKey);
	const cql = addOrderBy(query, ORDER_BY_CREATED);

	return searchContent(
		buildRequest({
			expand: includeBody ? QUESTION_EXPANSIONS : EXPAND_ALL,
			cql,
			cursor,
		}),
	);
};
